import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import SalesInvoiceAmendMutation from '../../../graphql/mutations/salesLedger/SalesInvoiceAmendMutation.graphql';
import { isPostedInvoice, SalesInvoiceFormAction } from './SalesInvoiceAction';
import BlurTextField from '../../../components/connectedForm/BlurTextField';
import getIn from '../../../components/connectedForm/utils/getIn';
import setIn from '../../../components/connectedForm/utils/setIn';
import { IFormAction } from '../../../@types/entityTypes';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { useComponentQueryErrorHandling } from '../../../utils/parseErrorResponse';
import {
  IAmendSalesInvoiceMutationResult,
  IAmendSalesInvoiceMutationVariables,
  ISalesInvoice,
  ISalesInvoiceInput,
} from '../../../graphql/schema/p4types';
import { BasicHeader } from '../../BasicHeader';
import Legend from '../../../components/connectedForm/Legend/Legend';
import Dropdown from '../../../components/connectedForm/Dropdown';
import { ModalBottomButtonContainer } from '../../../components/modals/Styles';
import { PrimaryButton, SecondaryButton } from '../../../components/connectedForm/Button';
import getClientMutationId from '../../../utils/getClientMutationId';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import Country from '../../../graphql/entities/company/Country';
import FluentModal from '../../../components/modals/FluentModal';

const AmendInvoiceTextField = ({ dataid, label, state }) => {
  const [invoice, setInvoice] = state;
  return (
    <BlurTextField
      label={label}
      value={getIn(invoice, dataid)}
      enableOnChange={true}
      onChange={(value) => {
        setInvoice((existing) => setIn(existing, dataid, value));
      }}
      data-testid={`amend-${dataid}`}
    />
  );
};
export const useAmendInvoiceAction: () => [IFormAction<SalesInvoiceFormAction>, ReactNode] = () => {
  const connectedForm = useContext(ConnectedFormContext);

  const handleQueryError = useComponentQueryErrorHandling();
  const [amendInvoice] = useMutation<IAmendSalesInvoiceMutationResult, IAmendSalesInvoiceMutationVariables>(
    SalesInvoiceAmendMutation,
    { onError: handleQueryError }
  );
  const [amendInvoiceModalOpen, setAmendInvoiceModalOpen] = useState(false);

  const salesInvoiceState = useState<ISalesInvoice>(connectedForm.values as ISalesInvoice);
  const [salesInvoice, setSalesInvoice] = salesInvoiceState;
  useEffect(() => {
    if (connectedForm.values) {
      setSalesInvoice(connectedForm.values as ISalesInvoice);
    }
  }, [connectedForm.values?.id, connectedForm.values?.uuid, connectedForm.values?.concurrencyVersion]);

  const amendInvoiceModal = (
    <FluentModal isOpen={amendInvoiceModalOpen} styles={{ main: { minWidth: '800px', paddingBottom: '60px' } }}>
      <BasicHeader>Amend Invoice</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Legend label="General" styles={{ root: { marginRight: 0 }, labelWrapper: { paddingTop: 0 } }} />
        <AmendInvoiceTextField dataid="documentReference" label={'Reference'} state={salesInvoiceState} />
        <Legend label="Invoice Account" styles={{ root: { marginRight: 0 } }} />
        <AmendInvoiceTextField dataid="invoiceCompany" label="Invoice Company" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="invoiceAddress.line1" label="Address" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="invoiceAddress.line2" label="" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="invoiceAddress.line3" label="" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="invoiceAddress.line4" label="" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="invoiceAddress.line5" label="" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="invoiceAddress.postCode" label="Post Code" state={salesInvoiceState} />
        <Dropdown
          label="Country"
          value={getIn(salesInvoice, 'invoiceAddress.country')}
          onChange={(value) => {
            setSalesInvoice((existing) => setIn(existing, 'invoiceAddress.country', value));
          }}
          {...Country.dropdown}
        />
        <Legend label="Delivery Address" styles={{ root: { marginRight: 0 } }} />
        <AmendInvoiceTextField dataid="deliveryName" label="Shipment Name" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="deliveryAddress.line1" label="Shipment Address" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="deliveryAddress.line2" label="" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="deliveryAddress.line3" label="" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="deliveryAddress.line4" label="" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="deliveryAddress.line5" label="" state={salesInvoiceState} />
        <AmendInvoiceTextField dataid="deliveryAddress.postCode" label="Post Code" state={salesInvoiceState} />
        <Dropdown
          label="Country"
          value={getIn(salesInvoice, 'deliveryAddress.country')}
          onChange={(value) => {
            setSalesInvoice((existing) => setIn(existing, 'deliveryAddress.country', value));
          }}
          {...Country.dropdown}
        />
        <ModalBottomButtonContainer>
          <SecondaryButton onClick={() => setAmendInvoiceModalOpen(false)}>Cancel</SecondaryButton>
          <PrimaryButton
            onClick={() => {
              amendInvoice({
                variables: {
                  input: {
                    clientMutationId: getClientMutationId(),
                    amendedInvoice: normalizeVariables(salesInvoice) as ISalesInvoiceInput,
                  },
                },
              }).then((result) => {
                if (result.data?.amendSalesInvoice) {
                  setAmendInvoiceModalOpen(false);
                  connectedForm.reloadData?.(true);
                }
              });
            }}
          >
            Save
          </PrimaryButton>
        </ModalBottomButtonContainer>
      </div>
    </FluentModal>
  );

  return [
    {
      key: 'amendInvoice',
      text: 'Amend Invoice',
      onClick: () => setAmendInvoiceModalOpen(true),
      condition: isPostedInvoice,
      actionReference: SalesInvoiceFormAction.AmendInvoice,
      'data-testid': 'sales-invoice-action-amend-invoice',
    },
    amendInvoiceModal,
  ];
};
